import React, { useEffect, useMemo } from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import { useObservable } from '@lib/common';
import { propertyQuery } from '@lib/state';
import { areaQuery, areaService, LocationType, PropertyDevice } from 'app/state';
import {
  formatValue,
  identifiersMatch,
  isGuid,
  normalizeIdentifier,
} from './device-detail-tabs-helpers';

interface AreaDetailsTabProps {
  device: PropertyDevice;
  paperClassName: string;
}

export function AreaDetailsTab({ device, paperClassName }: AreaDetailsTabProps) {
  const propertyId = useObservable(propertyQuery.activeProperty)?.id;
  const areas = useObservable(areaQuery.areas) ?? [];
  const isAreaDevice = device.thingLocationType === LocationType.Area;
  const identifier = device.thingIdentifier?.trim() ?? '';
  const isGuidIdentifier = isGuid(identifier);
  const normalizedIdentifier = normalizeIdentifier(identifier);

  const matchingArea = useMemo(() => {
    if (!identifier) return undefined;

    if (isGuidIdentifier) {
      return areas.find(area => identifiersMatch(area.id, normalizedIdentifier));
    }

    return areas.find(
      area =>
        identifiersMatch(area.id, normalizedIdentifier) ||
        identifiersMatch(area.name, normalizedIdentifier)
    );
  }, [areas, identifier, isGuidIdentifier, normalizedIdentifier]);

  useEffect(() => {
    if (!propertyId || !isAreaDevice || !identifier) return;

    const areaIdToHydrate = isGuidIdentifier ? identifier : matchingArea?.id;

    if (areaIdToHydrate) {
      // Always hydrate a resolved area via getAreaById because list/search responses do not include rooms.
      const hasHydratedRooms =
        identifiersMatch(matchingArea?.id, areaIdToHydrate) && Array.isArray(matchingArea?.rooms);

      if (!hasHydratedRooms) {
        areaService.getAreaById(propertyId, areaIdToHydrate);
      }
      return;
    }

    // Resolve non-GUID identifiers (e.g., area name) to an area id first.
    if (!isGuidIdentifier) {
      areaService.getAreas(propertyId, { name: identifier });
    }
  }, [propertyId, isAreaDevice, identifier, isGuidIdentifier, matchingArea]);

  const areaRooms = useMemo(
    () => (matchingArea?.rooms ?? []).map(room => room.roomNumber).filter(Boolean),
    [matchingArea]
  );

  return (
    <Paper elevation={4} className={paperClassName}>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          Location Information
        </Typography>
        {!isAreaDevice ? (
          <Typography color="textSecondary">This device is not assigned to an area.</Typography>
        ) : !matchingArea ? (
          <Typography color="textSecondary">
            Area details were not found for this device.
          </Typography>
        ) : (
          <Box>
            <Typography variant="subtitle1">Area Name</Typography>
            <Typography>{formatValue(matchingArea.name)}</Typography>

            <Box mt={2}>
              <Typography variant="subtitle1">Area Rooms</Typography>
              {areaRooms.length > 0 ? (
                <Typography>{areaRooms.join(', ')}</Typography>
              ) : (
                <Typography color="textSecondary">No rooms found for this area.</Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
